import { AllocationScenarioValuationModel, OPMCalculator } from 'api';
import { OPM } from 'common/constants/allocations';
import { ScalarSpreadsheetConfigs } from 'common/types/scalarSpreadsheet';
import { ValuationsApproach } from 'common/types/valuation';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { BACKSOLVE_TABLE_NAMES } from 'pages/Valuations/approaches/backsolveApproach/tableNames';
import { BACKSOLVE_APPROACH } from 'pages/Valuations/util/constants';
import { EQUITY_ALLOCATION_KEY } from 'pages/ValuationsAllocation/common/constants/equityAllocation';
import { FUTURE_EXIT_LABEL } from 'pages/ValuationsAllocation/common/constants/futureExit/futureExit';
import { FUTURE_EQUITY_SPREADSHEET_TABLE_NAMES } from 'pages/ValuationsAllocation/common/constants/futureExit/sheetConfigs';
import {
  buildBacksolveOPMLinkSource,
  buildFutureExitOPMLinkSource,
  getBacksolveOPMMethodologyCapTableId,
  getOPMSourceApproachRef,
  matchesOPMSourceApproachRef,
} from 'pages/ValuationsAllocation/components/ShowOPMCalculationsLink/opmCalculatorLinkSources';
import { OPMCalculatorLinkSource } from 'pages/ValuationsAllocation/components/ShowOPMCalculationsLink/ShowOPMCalculationsLink.types';
import { getNumberValue, getStringValue } from 'utilities';
import { getAllocationBacksolve } from '../getAllocationBacksolve';
import { getAllocationFutureExit } from '../getAllocationFutureExit';
import { getApproachTableName } from '../getApproachTableName';
import { getBacksolveScenarioMethods } from '../getBacksolveScenarioMethods';
import { getFutureExitScenarioMethod } from '../getFutureExitScenarioMethod';

/**
 * - `linked`: the source still prices with OPM; read the calculator's inputs off `source`.
 * - `gone`: the source was removed or stopped pricing with OPM -- unlink the calculator.
 * - `pending`: the source exists but its sheets have not evaluated yet -- try again later.
 *   Never treat this as `gone`: an unlink is permanent, and the workbook is rebuilt often.
 */
export type LinkedOPMCalculatorSourceResolution =
  | { status: 'linked'; source: OPMCalculatorLinkSource }
  | { status: 'gone' }
  | { status: 'pending' };

interface ResolveLinkedOPMCalculatorSourceParams {
  opmCalculator: Partial<OPMCalculator>;
  approaches: ValuationsApproach[];
  allocationScenarios: AllocationScenarioValuationModel[];
  workbook?: ScalarSpreadsheetConfigs | null;
}

const GONE = { status: 'gone' } as const;
const PENDING = { status: 'pending' } as const;

const resolveApproachSource = (
  { opmCalculator, approaches, workbook }: ResolveLinkedOPMCalculatorSourceParams,
  isBacksolve: boolean
): LinkedOPMCalculatorSourceResolution => {
  const approach = approaches.find(candidate =>
    matchesOPMSourceApproachRef(candidate, opmCalculator.source_approach_ref)
  );
  const backsolve = approach?.valuations_approach_backsolve;
  const futureExit = approach?.valuations_approach_future_exit;
  const pricesWithOPM = isBacksolve
    ? Boolean(backsolve) && getBacksolveScenarioMethods(backsolve ?? undefined).isBacksolveWithOPM
    : Boolean(futureExit) && getFutureExitScenarioMethod(futureExit ?? undefined).isFutureExitWithOPM;

  if (!approach || !pricesWithOPM) {
    return GONE;
  }

  const label = getStringValue(approach.name) || (isBacksolve ? BACKSOLVE_APPROACH : FUTURE_EXIT_LABEL);
  const approachRef = getOPMSourceApproachRef(approach);

  const source = isBacksolve
    ? buildBacksolveOPMLinkSource({
      label,
      approachRef,
      capTableId: getBacksolveOPMMethodologyCapTableId(backsolve, opmCalculator.cap_table),
      opmInputsSheetName: getApproachTableName({ approach, tableSuffix: BACKSOLVE_TABLE_NAMES.OPM_INPUTS }),
      summarySheetName: getApproachTableName({ approach, tableSuffix: BACKSOLVE_TABLE_NAMES.BACKSOLVE_SUMMARY }),
      opmDate: backsolve?.opm_backsolve_date,
    })
    : buildFutureExitOPMLinkSource({
      label,
      approachRef,
      sheetName: getApproachTableName({
        approach,
        tableSuffix: FUTURE_EQUITY_SPREADSHEET_TABLE_NAMES.ALLOCATION_METHOD_VALUE_OPM,
      }),
      futureEquitySheetName: getApproachTableName({
        approach,
        tableSuffix: FUTURE_EQUITY_SPREADSHEET_TABLE_NAMES.FUTURE_EQUITY_VALUE,
      }),
      exitDate: futureExit?.exit_date,
    });

  return workbook?.[source.sheetName] ? { status: 'linked', source } : PENDING;
};

/** The Equity Allocation cell (any company's sheet) whose link names one of `refs`. */
const findEquityAllocationLinkSource = (workbook: ScalarSpreadsheetConfigs, refs: Set<string>) => {
  const equityAllocationSheets = Object.entries(workbook).filter(([name]) => name.startsWith(EQUITY_ALLOCATION_KEY));

  const linked = equityAllocationSheets
    .flatMap(([, sheet]) => Object.values(((sheet as { cells?: Record<string, ICell> })?.cells ?? {}) as Record<string, ICell>))
    .find(cell => {
      const scenarioRef = cell?.opmCalculatorLink?.link?.scenarioRef;
      return Boolean(scenarioRef) && refs.has(getStringValue(scenarioRef));
    });

  return {
    hasSheets: equityAllocationSheets.length > 0,
    source: linked?.opmCalculatorLink as OPMCalculatorLinkSource | undefined,
  };
};

const resolveScenarioSource = ({
  opmCalculator,
  approaches,
  allocationScenarios,
  workbook,
}: ResolveLinkedOPMCalculatorSourceParams): LinkedOPMCalculatorSourceResolution => {
  const ref = getStringValue(opmCalculator.source_scenario_ref);
  const scenario = allocationScenarios.find(
    candidate => (candidate.id && String(candidate.id) === ref) || (candidate as { scenario_ref?: string }).scenario_ref === ref
  );

  if (scenario) {
    // The same rule that puts the link on the scenario's column.
    const { isBacksolveWithOPM, scenearioTypeIsBacksolve } = getAllocationBacksolve({ approaches, scenario });
    const { isFutureExitWithOPM, scenearioTypeIsFutureExit } = getAllocationFutureExit({ approaches, scenario });
    const pricesWithOPM
      = getNumberValue(scenario.scenario_method) === OPM
      || (scenearioTypeIsBacksolve && isBacksolveWithOPM)
      || (scenearioTypeIsFutureExit && isFutureExitWithOPM);

    if (!pricesWithOPM) {
      return GONE;
    }
  }

  if (!workbook) {
    return PENDING;
  }

  const refs = new Set([ref]);
  if (scenario?.id) {
    refs.add(String(scenario.id));
  }
  const scenarioRef = (scenario as { scenario_ref?: string } | undefined)?.scenario_ref;
  if (scenarioRef) {
    refs.add(scenarioRef);
  }

  const { hasSheets, source } = findEquityAllocationLinkSource(workbook, refs);
  if (source) {
    return { status: 'linked', source };
  }
  // A scenario of the valuation itself is known to exist, so a missing column is a sheet that is
  // still evaluating. One that is not (a related entity's) is gone once the sheets are there.
  return scenario || !hasSheets ? PENDING : GONE;
};

/** Where a linked OPM Calculator's inputs come from now, or whether its source is gone. */
const resolveLinkedOPMCalculatorSource = (
  params: ResolveLinkedOPMCalculatorSourceParams
): LinkedOPMCalculatorSourceResolution => {
  switch (params.opmCalculator.source_type) {
    case OPMCalculator.source_type.BACKSOLVE:
      return resolveApproachSource(params, true);
    case OPMCalculator.source_type.FUTURE_EXIT:
      return resolveApproachSource(params, false);
    case OPMCalculator.source_type.EQUITY_ALLOCATION:
      return resolveScenarioSource(params);
    default:
      return GONE;
  }
};

export default resolveLinkedOPMCalculatorSource;
